import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  createClinicGame,
  getClinicGames,
  updateClinicGame,
  deleteClinicGame,
} from "../../firebase";

/**
 * Predefined goal categories for games
 * These are hardcoded and the same for all clinics
 */
export const GAME_GOAL_CATEGORIES = [
  {
    id: "language_content",
    name: "שפה - תוכן (סמנטיקה)",
    color: "#3b82f6",
    goals: [
      { id: "vocabulary", name: "העשרת אוצר מילים: שמות עצם, פעלים, תארים" },
      { id: "categories", name: "קטגוריות והכללה: מיון פריטים, יוצא דופן" },
      { id: "semantic_relations", name: "קשרים סמנטיים: הפכים, נרדפות, סיבה ותוצאה" },
      { id: "concepts", name: "הבנת מושגים: מרחב, זמן, כמות" },
      { id: "belonging", name: "יחסי שייכות: של מי זה?, התאמת חפץ לבעליו" },
      { id: "ambiguity", name: "רב-משמעות: מילים עם יותר מפירוש אחד" },
    ],
  },
  {
    id: "language_form",
    name: "שפה - צורה (מורפולוגיה ותחביר)",
    color: "#22c55e",
    goals: [
      { id: "morphology", name: "מורפולוגיה: הטיות פעלים, יחיד/רבים, זכר/נקבה, שייכות" },
      { id: "prepositions", name: "מילות יחס: ב', ל', מ', את" },
      { id: "sentence_structure", name: "מבנה משפט: פשוט ומורכב עם מילות קישור" },
      { id: "questions", name: "שאילת שאלות: מי, מה, איפה, למה, איך" },
    ],
  },
  {
    id: "language_use",
    name: "שפה - שימוש (פרגמטיקה ותקשורת)",
    color: "#8b5cf6",
    goals: [
      { id: "conversation", name: "כישורי שיחה: שמירה על נושא, יוזמה, תיקון" },
      { id: "nonverbal", name: "תקשורת בלתי מילולית: הבעות פנים, שפת גוף" },
      { id: "social_problem", name: "פתרון בעיות חברתיות: מה עושים כש..." },
      { id: "theory_of_mind", name: "תיאוריית המיינד: הבנת נקודת מבט האחר" },
      { id: "implied_language", name: "שפה משתמעת: ציניות, הומור, מטאפורות" },
      { id: "game_rules", name: "חוקי משחק: הקשבה, תור, התמודדות עם הפסד" },
    ],
  },
  {
    id: "speech_phonology",
    name: "דיבור ופונולוגיה",
    color: "#f59e0b",
    goals: [
      { id: "phonological_awareness", name: "מודעות פונולוגית: חריזה, פירוק והרכבה" },
      { id: "articulation", name: "היגוי: תרגול הגאים ספציפיים" },
      { id: "fluency", name: "שטף הדיבור: הפחתת מתח, דיבור נינוח" },
      { id: "prosody", name: "פרוזודיה: אינטונציה, הטעמה וקצב" },
    ],
  },
  {
    id: "narrative",
    name: "שיח ונרטיב (סיפוריות)",
    color: "#ec4899",
    goals: [
      { id: "sequence", name: "רצף אירועים: סידור תמונות כרונולוגי" },
      { id: "description", name: "תיאור תמונה: פרטים ופעולות" },
      { id: "story_structure", name: "בניית סיפור: רקע, בעיה, פתרון" },
      { id: "inference", name: "הסקת מסקנות: הבנת מידע לא מפורש" },
    ],
  },
  {
    id: "literacy",
    name: "אוריינות והכנה לכיתה א'",
    color: "#06b6d4",
    goals: [
      { id: "letter_recognition", name: "זיהוי אותיות: קשר אות-צליל" },
      { id: "early_reading", name: "ראשית קריאה: פענוח מילים" },
      { id: "writing", name: "כתיבה: העתקה, כתיבה פונולוגית" },
      { id: "visual_memory", name: "זיכרון חזותי: תבניות מילים" },
    ],
  },
  {
    id: "executive_functions",
    name: "תפקודים ניהוליים וקוגניציה",
    color: "#6b7280",
    goals: [
      { id: "working_memory", name: "זיכרון עבודה: שמיעתי או חזותי" },
      { id: "attention", name: "קשב וריכוז: מתמשך, סלקטיבי, מפוצל" },
      { id: "inhibition", name: "אינהיביציה: עצירת תגובה אוטומטית" },
      { id: "flexibility", name: "גמישות מחשבתית: מעבר בין חוקים" },
      { id: "planning", name: "תכנון וארגון: שלבי עבודה למטרה" },
    ],
  },
  {
    id: "motor_sensory",
    name: "מוטוריקה ותחושה",
    color: "#ef4444",
    goals: [
      { id: "fine_motor", name: "מוטוריקה עדינה: אחיזה, השחלה" },
      { id: "oral_motor", name: "מוטוריקה אורלית: נשיפה, חיזוק אברי דיבור" },
      { id: "sensory_regulation", name: "ויסות חושי: מרקמים, רמות רעש" },
    ],
  },
];

// Helper function to get all goals as a flat array with category info
export const getAllGoals = () => {
  const allGoals = [];
  GAME_GOAL_CATEGORIES.forEach((category) => {
    category.goals.forEach((goal) => {
      allGoals.push({
        ...goal,
        categoryId: category.id,
        categoryName: category.name,
        categoryColor: category.color,
      });
    });
  });
  return allGoals;
};

// Helper function to get goal details by ID
export const getGoalById = (goalId) => {
  return getAllGoals().find((g) => g.id === goalId);
};

// Helper function to get goals by IDs
export const getGoalsByIds = (goalIds) => {
  const allGoals = getAllGoals();
  return goalIds.map((id) => allGoals.find((g) => g.id === id)).filter(Boolean);
};

export const gamesApi = createApi({
  reducerPath: "gamesApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Games"],
  endpoints: (builder) => ({
    // Get all games for a clinic
    getClinicGames: builder.query({
      async queryFn({ clinicId }) {
        try {
          const games = await getClinicGames(clinicId);
          return { data: games };
        } catch (error) {
          console.error("[CK-8400] Error fetching clinic games:", {
            errorCode: "CK-8400",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Games", id })),
              { type: "Games", id: "LIST" },
            ]
          : [{ type: "Games", id: "LIST" }],
    }),

    // Create a new game
    createClinicGame: builder.mutation({
      async queryFn({ clinicId, gameData }) {
        try {
          const game = await createClinicGame(clinicId, gameData);
          return { data: game };
        } catch (error) {
          console.error("[CK-8401] Error creating clinic game:", {
            errorCode: "CK-8401",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "Games", id: "LIST" }],
    }),

    // Update a game
    updateClinicGame: builder.mutation({
      async queryFn({ clinicId, gameId, gameData }) {
        try {
          const game = await updateClinicGame(clinicId, gameId, gameData);
          return { data: game };
        } catch (error) {
          console.error("[CK-8402] Error updating clinic game:", {
            errorCode: "CK-8402",
            errorMessage: error.message,
            clinicId,
            gameId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { gameId }) => [
        { type: "Games", id: gameId },
        { type: "Games", id: "LIST" },
      ],
    }),

    // Delete a game
    deleteClinicGame: builder.mutation({
      async queryFn({ clinicId, gameId }) {
        try {
          await deleteClinicGame(clinicId, gameId);
          return { data: gameId };
        } catch (error) {
          console.error("[CK-8403] Error deleting clinic game:", {
            errorCode: "CK-8403",
            errorMessage: error.message,
            clinicId,
            gameId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "Games", id: "LIST" }],
    }),
  }),
});

export const {
  useGetClinicGamesQuery,
  useCreateClinicGameMutation,
  useUpdateClinicGameMutation,
  useDeleteClinicGameMutation,
} = gamesApi;
